import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, TrendingUp, Clock, Shield, Brain, FileCheck, Zap } from 'lucide-react';
import { usePlatformStore } from '../../stores/platformStore';
import { fullAgentSuite, enhancedWorkflows, workflowExecutions, agentWorkload } from '../../data/enhancedAgentsData';

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change?: string;
  icon: React.ReactNode;
  color: string;
  highlight?: boolean;
}> = ({ title, value, change, icon, color, highlight = false }) => (
  <div className={`metric-card ${highlight ? 'border-2' : ''}`} style={{ 
    borderColor: highlight ? color : undefined,
    position: 'relative'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
      <div>
        <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {title}
        </div>
        <div className="metric-value">
          {value}
        </div>
        {change && (
          <div className="metric-change" style={{ color: color }}>
            {change}
          </div>
        )}
      </div>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        background: `${color}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color,
      }}>
        {icon}
      </div>
    </div>
  </div>
);

const WorkflowCard: React.FC<{
  workflow: any;
  executions: any[];
}> = ({ workflow, executions }) => {
  const navigate = useNavigate();
  const activeExecs = executions.filter(e => e.status === 'processing').length;
  const completedToday = executions.filter(e => e.status === 'completed').length;
  
  const getOrchestrationInfo = (type: string) => {
    switch (type) {
      case 'native':
        return {
          label: 'Native Agent Orchestration',
          color: '#3b82f6',
          description: 'ElevateNow certified agents'
        };
      case 'hosted':
        return {
          label: 'Hosted Agent Integration',
          color: '#9ca3af',
          description: 'AWS Agent Code integration'
        };
      case 'hybrid':
        return {
          label: 'Hybrid Intelligence',
          color: '#3b82f6',
          description: 'Native + hosted coordination'
        };
      default:
        return {
          label: 'Standard Workflow',
          color: 'var(--text-secondary)',
          description: 'Basic orchestration'
        };
    }
  };
  
  const orchestration = getOrchestrationInfo(workflow.orchestrationType);
  
  return (
    <div 
      onClick={() => navigate(`/workflows/${workflow.id}?tab=execute`)}
      className={`workflow-card ${workflow.orchestrationType}`}
      style={{
        cursor: 'pointer',
        transition: 'all 0.3s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-card)';
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ fontWeight: '600', marginBottom: '4px', fontSize: '16px', color: 'var(--text-primary)' }}>
            {workflow.name}
          </h4>
          <div style={{ fontSize: '12px', color: orchestration.color, fontWeight: '500', marginBottom: '6px' }}>
            {orchestration.label}
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.4', marginBottom: '8px' }}>
            {workflow.description}
          </p>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {orchestration.description}
          </div>
        </div>
        <div style={{
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '10px',
          fontWeight: '600',
          background: workflow.status === 'active' ? 'var(--status-active)20' : 'var(--status-warning)20',
          color: workflow.status === 'active' ? 'var(--status-active)' : 'var(--status-warning)',
          border: `1px solid ${workflow.status === 'active' ? 'var(--status-active)' : 'var(--status-warning)'}40`,
          textTransform: 'uppercase',
        }}>
          {workflow.status}
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', fontSize: '13px', marginBottom: '16px' }}>
        <div>
          <span style={{ color: 'var(--text-muted)' }}>Active:</span>
          <span style={{ fontWeight: '600', marginLeft: '6px', color: activeExecs > 0 ? 'var(--status-processing)' : 'inherit' }}>
            {activeExecs}
          </span>
        </div>
        <div>
          <span style={{ color: 'var(--text-muted)' }}>Completed:</span>
          <span style={{ fontWeight: '600', marginLeft: '6px' }}>{completedToday}</span>
        </div>
        <div>
          <span style={{ color: 'var(--text-muted)' }}>Success:</span>
          <span style={{ fontWeight: '600', marginLeft: '6px', color: 'var(--status-active)' }}>
            {workflow.metrics.successRate}%
          </span>
        </div>
      </div>
      
      <div style={{ marginBottom: '16px' }}>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '6px' }}>
          Agent Pipeline ({workflow.steps.length} steps)
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {workflow.steps.map((step: any, idx: number) => (
            <div
              key={step.id}
              style={{
                flex: 1,
                height: '6px',
                background: idx < 3 ? orchestration.color : 'var(--border-color)',
                borderRadius: '3px',
                transition: 'all 0.3s',
              }}
              title={`${step.name} - ${step.agentId}`}
            />
          ))}
        </div>
      </div>
      
      <div style={{ 
        paddingTop: '12px', 
        borderTop: '1px solid var(--border-color)',
        fontSize: '12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ color: 'var(--text-muted)' }}>
          {workflow.metrics.totalExecutions.toLocaleString()} total executions
        </div>
        <div style={{ 
          color: orchestration.color,
          fontWeight: '600',
          opacity: 0.9
        }}>
          Click to monitor live →
        </div>
      </div>
    </div>
  );
};

const AgentCard: React.FC<{
  agent: any;
  workload?: any;
}> = ({ agent, workload }) => {
  const navigate = useNavigate();
  const utilization = workload ? Math.round((workload.current / workload.capacity) * 100) : 0;
  const utilizationColor = utilization > 80 ? 'var(--status-error)' : utilization > 60 ? 'var(--status-warning)' : 'var(--status-active)';
  
  const getProviderInfo = (provider: string) => {
    switch (provider) {
      case 'native':
        return {
          color: '#3b82f6',
          label: 'Native',
          badge: 'ElevateNow'
        };
      case 'hosted':
        return {
          color: '#9ca3af',
          label: 'Hosted',
          badge: agent.provenance?.vendor || 'External'
        };
      default:
        return {
          color: 'var(--text-secondary)',
          label: 'Standard',
          badge: 'Platform'
        };
    }
  };
  
  const providerInfo = getProviderInfo(agent.provider);
  
  return (
    <div
      onClick={() => navigate(`/orchestrator/${agent.id}`)}
      className="agent-card"
      style={{
        background: agent.status === 'processing' ? `${providerInfo.color}10` : 'var(--card-bg)',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <div style={{ fontWeight: '600', fontSize: '14px' }}>{agent.name}</div>
            <div className={`provider-badge ${agent.provider}`}>
              {providerInfo.label}
            </div>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {agent.type} • {providerInfo.badge}
          </div>
        </div>
        <Brain size={18} style={{ color: providerInfo.color }} />
      </div>
      
      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px', minHeight: '36px', lineHeight: '1.3' }}>
        {agent.description}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
          <span>Utilization</span>
          <span style={{ color: utilizationColor, fontWeight: '600' }}>{utilization}%</span>
        </div>
        <div className="progress-bar">
          <div 
            className={`progress-fill ${utilization > 80 ? 'warning' : 'success'}`}
            style={{
              width: `${utilization}%`,
              background: utilizationColor,
            }} 
          />
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '11px', marginBottom: '10px' }}>
        <div>
          <span style={{ color: 'var(--text-muted)' }}>Success:</span>
          <span style={{ fontWeight: '600', marginLeft: '4px' }}>{agent.performance.successRate}%</span>
        </div>
        <div>
          <span style={{ color: 'var(--text-muted)' }}>Avg:</span>
          <span style={{ fontWeight: '600', marginLeft: '4px' }}>{agent.performance.avgProcessingTime}s</span>
        </div>
      </div>
      
      {agent.provenance && (
        <div style={{ 
          paddingTop: '8px', 
          borderTop: '1px solid var(--border-color)',
          fontSize: '10px',
          color: providerInfo.color,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{agent.performance.tasksCompleted.toLocaleString()} tasks</span>
          <span>v{agent.provenance.version}</span>
        </div>
      )}
    </div>
  );
};

export const EnhancedOperationsDashboard: React.FC = () => {
  const { setAgents, addProcessingEvent } = usePlatformStore();
  const [liveMetrics, setLiveMetrics] = useState({
    submissionsPerHour: 347,
    avgProcessingTime: '2.1 min',
    crossPlatformConsistency: '94.2%',
    activeWorkflows: 3,
    orchestrationHealth: '98.4%',
    governanceScore: '99.1%',
  });

  useEffect(() => {
    setAgents(fullAgentSuite);
    
    const interval = setInterval(() => {
      setLiveMetrics(prev => ({
        submissionsPerHour: Math.floor(Math.random() * 50) + 320,
        avgProcessingTime: `${(Math.random() * 1.5 + 1.8).toFixed(1)} min`,
        crossPlatformConsistency: `${(Math.random() * 2.5 + 92.5).toFixed(1)}%`,
        activeWorkflows: 3,
        orchestrationHealth: `${(Math.random() * 2 + 97.5).toFixed(1)}%`,
        governanceScore: `${(Math.random() * 1.5 + 98.5).toFixed(1)}%`,
      }));
      
      const eventTypes = ['workflow_start', 'step_complete', 'agent_coordination', 'governance_check', 'workflow_complete'];
      const workflows = ['native-orchestration', 'hosted-integration', 'hybrid-governance'];
      
      addProcessingEvent({
        id: `event-${Date.now()}`,
        timestamp: new Date(),
        type: eventTypes[Math.floor(Math.random() * eventTypes.length)] as any,
        workflowId: workflows[Math.floor(Math.random() * workflows.length)],
        data: { auto: true },
      });
    }, 4000);
    
    return () => clearInterval(interval);
  }, [setAgents, addProcessingEvent]);

  return (
    <div style={{ padding: '20px', background: 'var(--divider-light)', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', marginBottom: '8px', fontWeight: '700', color: 'var(--text-primary)' }}>
          Operations Dashboard
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
          Real-time monitoring and management of insurance workflows and agent systems
        </p>
      </div>

      {/* Enhanced Metrics Row - BUILD/HOST/GOVERN KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <MetricCard
          title="Submissions/Hour"
          value={liveMetrics.submissionsPerHour}
          change="Current rate"
          icon={<Activity size={20} />}
          color="var(--accent-primary)"
        />
        <MetricCard
          title="Processing Time"
          value={liveMetrics.avgProcessingTime}
          change="Average time"
          icon={<Clock size={20} />}
          color="var(--status-active)"
        />
        <MetricCard
          title="Decision Consistency"
          value={liveMetrics.crossPlatformConsistency}
          change="System wide"
          icon={<TrendingUp size={20} />}
          color="var(--accent-light)"
          highlight={true}
        />
        <MetricCard
          title="Active Workflows"
          value={`${liveMetrics.activeWorkflows}/3`}
          change="Currently running"
          icon={<Zap size={20} />}
          color="var(--accent-primary)"
        />
        <MetricCard
          title="Platform Health"
          value={liveMetrics.orchestrationHealth}
          change="System status"
          icon={<FileCheck size={20} />}
          color="var(--status-active)"
        />
        <MetricCard
          title="Governance Score"
          value={liveMetrics.governanceScore}
          change="Compliance status"
          icon={<Shield size={20} />}
          color="var(--accent-dark)"
          highlight={true}
        />
      </div>

      {/* Active Workflows - BUILD/HOST/HYBRID Showcase */}
      <div className="sn-panel" style={{ marginBottom: '24px' }}>
        <div className="sn-panel-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Active Workflows</span>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              BUILD • HOST • GOVERN orchestration patterns
            </div>
          </div>
        </div>
        <div className="sn-panel-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            {enhancedWorkflows.map((workflow) => {
              const executions = workflowExecutions.find(w => w.workflowId === workflow.id)?.executions || [];
              return <WorkflowCard key={workflow.id} workflow={workflow} executions={executions} />;
            })}
          </div>
        </div>
      </div>

      {/* AI Agent Fleet - Native vs Hosted */}
      <div className="sn-panel" style={{ marginBottom: '24px' }}>
        <div className="sn-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Insurance Intelligence Agent Fleet</span>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div className="provider-badge native">Native</div>
              <span>9 agents</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div className="provider-badge hosted">Hosted</div>
              <span>3 agents</span>
            </div>
          </div>
        </div>
        <div className="sn-panel-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            {fullAgentSuite.map((agent) => (
              <AgentCard 
                key={agent.id} 
                agent={agent} 
                workload={agentWorkload[agent.id as keyof typeof agentWorkload]}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Actionable Alerts Monitor */}
      <div className="sn-panel">
        <div className="sn-panel-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Operational Alerts</span>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Items requiring operator attention
            </div>
          </div>
        </div>
        <div className="sn-panel-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
            
            {/* Workflow Alerts */}
            <div>
              <h5 style={{ fontSize: '14px', marginBottom: '12px', color: 'var(--text-primary)', fontWeight: '600' }}>
                Workflow Issues
              </h5>
              <div style={{ fontSize: '12px' }}>
                <div className="metric-card" style={{ 
                  marginBottom: '12px',
                  textAlign: 'left',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                      Claims workflow timeout
                    </div>
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '9px', 
                      fontWeight: '600',
                      background: 'var(--status-error)',
                      color: 'white'
                    }}>
                      CRITICAL
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '12px' }}>
                    Step 3 exceeded timeout threshold (18 minutes)
                  </div>
                  <button className="sn-button" style={{ fontSize: '10px', padding: '6px 12px' }}>
                    Force Retry
                  </button>
                </div>
                
                <div className="metric-card" style={{ 
                  marginBottom: '12px',
                  textAlign: 'left',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                      High retry rate detected
                    </div>
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '9px', 
                      fontWeight: '600',
                      background: 'var(--status-warning)',
                      color: 'white'
                    }}>
                      WARNING
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '12px' }}>
                    Regulatory filing - 12 retries in past hour
                  </div>
                  <button className="sn-button" style={{ fontSize: '10px', padding: '6px 12px' }}>
                    Investigate
                  </button>
                </div>

                <div className="metric-card" style={{ 
                  textAlign: 'left',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                      Queue depth elevated
                    </div>
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '9px', 
                      fontWeight: '600',
                      background: 'var(--accent-primary)',
                      color: 'white'
                    }}>
                      INFO
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '12px' }}>
                    Submission intake - 47 pending items
                  </div>
                  <button className="sn-button" style={{ fontSize: '10px', padding: '6px 12px' }}>
                    Scale Resources
                  </button>
                </div>
              </div>
            </div>
            
            {/* Agent Alerts */}
            <div>
              <h5 style={{ fontSize: '14px', marginBottom: '12px', color: 'var(--text-primary)', fontWeight: '600' }}>
                Agent Issues
              </h5>
              <div style={{ fontSize: '12px' }}>
                <div className="metric-card" style={{ 
                  marginBottom: '12px',
                  textAlign: 'left',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                      FraudDetector unavailable
                    </div>
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '9px', 
                      fontWeight: '600',
                      background: 'var(--status-error)',
                      color: 'white'
                    }}>
                      CRITICAL
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '12px' }}>
                    AWS Agent Code connection lost - 8 min ago
                  </div>
                  <button className="sn-button" style={{ fontSize: '10px', padding: '6px 12px' }}>
                    Reconnect
                  </button>
                </div>

                <div className="metric-card" style={{ 
                  marginBottom: '12px',
                  textAlign: 'left',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                      InsurVerify performance degraded
                    </div>
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '9px', 
                      fontWeight: '600',
                      background: 'var(--status-warning)',
                      color: 'white'
                    }}>
                      WARNING
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '12px' }}>
                    Response time exceeds 30s threshold
                  </div>
                  <button className="sn-button" style={{ fontSize: '10px', padding: '6px 12px' }}>
                    Check Logs
                  </button>
                </div>

                <div className="metric-card" style={{ 
                  textAlign: 'left',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                      Model update available
                    </div>
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '9px', 
                      fontWeight: '600',
                      background: 'var(--accent-primary)',
                      color: 'white'
                    }}>
                      INFO
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '12px' }}>
                    DocumentExtractor v3.2.1 → v3.3.0
                  </div>
                  <button className="sn-button" style={{ fontSize: '10px', padding: '6px 12px' }}>
                    Schedule Update
                  </button>
                </div>
              </div>
            </div>
            
            {/* System Alerts */}
            <div>
              <h5 style={{ fontSize: '14px', marginBottom: '12px', color: 'var(--text-primary)', fontWeight: '600' }}>
                System Health
              </h5>
              <div style={{ fontSize: '12px' }}>
                <div className="metric-card" style={{ 
                  marginBottom: '12px',
                  textAlign: 'left',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                      Database pool capacity high
                    </div>
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '9px', 
                      fontWeight: '600',
                      background: 'var(--status-warning)',
                      color: 'white'
                    }}>
                      WARNING
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '12px' }}>
                    95/100 connections - approaching limit
                  </div>
                  <button className="sn-button" style={{ fontSize: '10px', padding: '6px 12px' }}>
                    Scale Pool
                  </button>
                </div>

                <div className="metric-card" style={{ 
                  marginBottom: '12px',
                  textAlign: 'left',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                      Memory usage trending up
                    </div>
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '9px', 
                      fontWeight: '600',
                      background: 'var(--accent-primary)',
                      color: 'white'
                    }}>
                      INFO
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '12px' }}>
                    Node-2: 78% usage (+15% in 1hr)
                  </div>
                  <button className="sn-button" style={{ fontSize: '10px', padding: '6px 12px' }}>
                    Monitor Trend
                  </button>
                </div>

                <div className="metric-card" style={{ 
                  textAlign: 'left',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                      Backup process completed
                    </div>
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '9px', 
                      fontWeight: '600',
                      background: 'var(--status-active)',
                      color: 'white'
                    }}>
                      SUCCESS
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '11px', marginBottom: '12px' }}>
                    Daily backup finished - 2.3GB archived
                  </div>
                  <button className="sn-button" style={{ fontSize: '10px', padding: '6px 12px' }}>
                    Verify Backup
                  </button>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
};